import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Field, TextAreaField, SelectField, Honeypot, SuccessScreen, SubmitButton } from "@/components/FormKit";
import { BrochureCTA } from "@/components/BrochureCTA";
import { postJSON } from "@/lib/api";
import { SUBJECTS } from "@/data/content";

export default function Tutor() {
  const [f, setF] = useState({ name: "", email: "", subject: "", qualifications: "", experience: "", portfolio_link: "", motivation: "" });
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.subject) {
      toast.error("Please select a subject.");
      return;
    }
    setLoading(true);
    try {
      await postJSON("/tutor", { ...f, company });
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tutor-page">
      <PageHero
        n="09"
        label="Become a Tutor"
        lines={["Teach Where", "It Counts"]}
        intro="Mentor the next generation of researchers within the NextGen Training Program (NTP)."
      />

      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <SectionLabel n="◆">The tutor's role in NTP</SectionLabel>
            <p className="text-white/70 text-lg leading-relaxed mt-8">
              NTP tutors are more than teachers — they are research mentors. You'll guide students through advanced problem solving, innovation projects, olympiad preparation and university-admission strategy.
            </p>
            <p className="text-white/55 leading-relaxed mt-6">
              We're looking for subject specialists who believe that every engaged student can become a problem solver, irrespective of starting ability.
            </p>
            <div className="mt-10">
              <BrochureCTA
                label="NTP Tutor Handbook"
                eyebrow="Downloadable · one-pager"
                note="Programme overview, expectations of an NTP mentor, subject scope and how the CoPE-App supports your daily teaching."
                testid="tutor-brochure"
              />
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <SuccessScreen testid="tutor-success" title="Application received." body="Thank you for applying. Our team will review your profile and be in touch about NTP opportunities." />
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="tutor-form">
                <Honeypot value={company} onChange={(e) => setCompany(e.target.value)} />
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Full name" id="name" value={f.name} onChange={set("name")} placeholder="Your name" required />
                  <Field label="Email" id="email" type="email" value={f.email} onChange={set("email")} placeholder="you@email.com" required />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <SelectField label="Subject" id="subject" value={f.subject} onChange={set("subject")} options={SUBJECTS} placeholder="Select subject" />
                  <Field label="Experience" id="experience" value={f.experience} onChange={set("experience")} placeholder="e.g. 6 years" required />
                </div>
                <Field label="Qualifications" id="qualifications" value={f.qualifications} onChange={set("qualifications")} placeholder="Degrees, certifications…" required />
                <Field label="Portfolio link" id="portfolio_link" value={f.portfolio_link} onChange={set("portfolio_link")} placeholder="https:// (optional)" />
                <TextAreaField label="Why do you want to teach with us?" id="motivation" value={f.motivation} onChange={set("motivation")} placeholder="A few lines about your motivation…" />
                <SubmitButton loading={loading} testid="tutor-submit">Submit Application</SubmitButton>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
